import { sendToApi } from 'api/client'
import { PaginatedApiClient } from 'api/PaginatedApiClient'
import { NavSortOrder, ArticleFlat, AlmanacFile as File } from 'types'

interface AccessGrant {
  accessor_type: 'User' | 'Channel' | 'Company'
  accessor_id: number
  permission: string
}

const updateFolder = (folderId: string, params: object) =>
  sendToApi<{ folder: File }>(`/folders/${folderId}`, {
    body: {
      folder: {
        ...params,
      },
    },
    method: 'PUT',
  })

export interface CreateFolderProps {
  title: string
  emoji?: string
  workspaceKey: string
  parentKey?: string
  parentId?: number | null
  accessGrants?: AccessGrant[]
  defaultView?: string
  createFolderViewEvent?: boolean
  handbookMode?: boolean
  databaseMode?: boolean
  pinned?: boolean
  shared?: boolean
}

export function isTableOrBoard(chosenOption: string): chosenOption is 'table_view' | 'board_view' {
  return ['table_view', 'board_view'].includes(chosenOption)
}

export const FolderClient = {
  createFolder: ({
    title,
    emoji,
    workspaceKey,
    parentKey,
    parentId,
    accessGrants,
    defaultView,
    createFolderViewEvent,
    handbookMode,
    databaseMode,
    pinned,
    shared,
  }: CreateFolderProps) =>
    sendToApi<{ folder: File }>(`/folders`, {
      body: {
        folder: {
          parent_key: parentKey,
          parent_id: parentId,
          title,
          emoji,
          workspace_key: workspaceKey,
          access_grants: accessGrants,
          default_view: defaultView,
          create_folder_view_event: createFolderViewEvent,
          handbook_mode: handbookMode,
          database_mode: databaseMode,
          added_to_pinned: pinned,
          added_to_shared: shared,
        },
      },
      method: 'POST',
    }),

  updateFolder,

  getNestedFiles: (projectId: string, sortOrder: NavSortOrder, workspaceId: number) =>
    sendToApi<{ files: File[] }>(
      `/folders/${projectId}/nested_files?sort_order=${sortOrder}&workspace_id=${workspaceId}`,
    ),

  getFolders: (workspaceId: number) =>
    new PaginatedApiClient<{ folders: File[] }>(`/w/${workspaceId}/all_workspace_folders`),

  getProjectNestedFolders: (folderId: string) =>
    new PaginatedApiClient<{ folders: File[] }>(`/folders/${folderId}/nested_folders?paginated=true`),

  getFolder: (folderId: string) => sendToApi<{ folder: File }>(`/folders/${folderId}`),

  renameFolder: (folderId: string, title: string) => updateFolder(folderId, { title }),

  checkIfDocAlreadyExists: (folderId: string, articleId: string) =>
    sendToApi<{ in_folder: boolean }>(`/folders/${folderId}/is_doc_in_folder`, {
      body: {
        article_id: articleId,
      },
      method: 'POST',
    }),

  destroyFolder: (folderId: string) =>
    sendToApi<void>(`/folders/${folderId}`, {
      method: 'DELETE',
    }),

  bulkDestroyFiles: (folderIds: string[], articleIds: string[], almanacFileIds: string[]) =>
    sendToApi<void>(`/folders/batch_destroy`, {
      body: {
        folder_ids: folderIds,
        article_ids: articleIds,
        almanac_file_ids: almanacFileIds,
      },
      method: 'DELETE',
    }),

  bulkMoveFiles: ({
    folderIds,
    articleIds,
    fileIds,
    destinationFolderId,
    destinationWorkspaceId,
    originalWorkspaceId,
  }: {
    folderIds: string[]
    articleIds: string[]
    fileIds: string[]
    destinationFolderId: string | null
    destinationWorkspaceId: number
    originalWorkspaceId: number
  }) =>
    sendToApi<void | string[]>(`/folders/batch_move`, {
      body: {
        folder_ids: folderIds,
        article_ids: articleIds,
        file_ids: fileIds,
        destination_folder_id: destinationFolderId,
        destination_workspace_id: destinationWorkspaceId,
        original_workspace_id: originalWorkspaceId,
      },
      method: 'PUT',
    }),

  bulkCopyFiles: ({
    folderIds,
    articleIds,
    fileIds,
    destinationFolderId,
    destinationWorkspaceId,
    articleVersions,
    articleNames,
    folderNames,
    fileNames,
    shareWithSamePeople = false,
    fromTemplate = false,
    position,
  }: {
    folderIds: string[]
    articleIds: string[]
    fileIds: string[]
    destinationFolderId: string | null
    destinationWorkspaceId: number
    articleVersions?: { [key: string]: number }
    articleNames?: string[]
    folderNames?: string[]
    fileNames?: string[]
    shareWithSamePeople?: boolean
    fromTemplate?: boolean
    position?: number | null
  }) =>
    sendToApi<{ files: ArticleFlat[] }>(`/folders/batch_copy`, {
      body: {
        folder_ids: folderIds,
        article_ids: articleIds,
        file_ids: fileIds,
        article_names: articleNames,
        folder_names: folderNames,
        file_names: fileNames,
        destination_folder_id: destinationFolderId,
        destination_workspace_id: destinationWorkspaceId,
        article_versions: articleVersions,
        share_with_same_people: shareWithSamePeople,
        from_template: fromTemplate,
        position,
      },
      method: 'PUT',
    }),
}
